import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FinancialProductRulesEditDetailsIncomesTable from './FinancialProductRulesEditDetailsIncomesTable';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { FINANCIAL_PRODUCT_RULE_INCOME_TYPE_LIST_QUERY } from '../../../queries';
import { getInt } from 'utils/functions';
import { useRouteMatch } from 'react-router-dom';
import { useToast } from '@totalsoft/rocket-ui';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { ADD_UPDATE_FINANCIAL_PRODUCT_RULE_INCOME, REMOVE_FINANCIAL_PRODUCT_RULE_INCOME } from '../../../mutations';
import { FINANCIAL_PRODUCT_RULE_INCOME_UPDATED, FINANCIAL_PRODUCT_RULE_INCOME_REMOVED } from '../../../subscriptions';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { financialProductRuleIncomeValidator } from '../../../validators';
import { CURRENCIES_DICTIONARY_QUERY } from 'features/common/queries';
import { FINANCIAL_PRODUCT_QUERY } from 'features/financialProducts/edit/queries';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';

const FinancialProductRulesEditDetailsIncomesContainer = ({ canWrite, incomeList, isSaved, productId }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const match = useRouteMatch();
  const ruleId = match.params.ruleId && getInt(match.params.ruleId);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);

  useEffect(() => {
    resetLens(
      incomeList?.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        elementLocal.isSaving = false;
        return elementLocal;
      }) || []
    );
  }, [resetLens, incomeList]);

  const { data: dataProduct } = useQuery(FINANCIAL_PRODUCT_QUERY, {
    variables: { productId },
    skip: !productId,
    fetchPolicy: 'cache-only'
  });

  const { data: currenciesDictionaryData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  const { data: financialProductRuleIncomeTypeList } = useQuery(FINANCIAL_PRODUCT_RULE_INCOME_TYPE_LIST_QUERY);

  const [financialProductRuleIncomeValidation, validateFinancialProductRuleIncome] =
    useDirtyFieldValidation(financialProductRuleIncomeValidator);

  useEffect(() => {
    validateFinancialProductRuleIncome(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validateFinancialProductRuleIncome]);

  const productCurrency = currenciesDictionaryData?.currenciesDictionary?.find(
    item => item.id === dataProduct?.financialProduct?.leasingFinancialProduct?.creditCurrencyId
  );
  const handleAdd = useCallback(() => {
    set(lens)([
      ...(lens |> get),
      {
        financialProductRuleIncomeId: null,
        readOnlyYN: false,
        incomeTypeId: null,
        incomeTypeName: '',
        value: 0,
        currencyId: productCurrency?.id,
        currencyCode: productCurrency?.code,
        implicit: false,
        mandatory: false,
        isInEditing: true
      }
    ]);
  }, [lens, productCurrency]);

  const handleCloseEdit = useCallback(
    orderNo => {
      const lensLocal = [...(lens |> get)];
      lensLocal.forEach((element, index) => {
        if (index === orderNo) {
          if (element.financialProductRuleIncomeId === null) {
            lensLocal.splice(orderNo, 1);
            set(lens)(lensLocal);
          } else {
            const unmodifiedElementList = incomeList.filter(el => el.financialProductRuleIncomeId === element.financialProductRuleIncomeId);
            if (unmodifiedElementList.length > 0) {
              const unmodifiedElement = unmodifiedElementList[0];
              element.readOnlyYN = unmodifiedElement.readOnlyYN;
              element.incomeTypeId = unmodifiedElement.incomeTypeId;
              element.incomeTypeName = unmodifiedElement.incomeTypeName;
              element.value = unmodifiedElement.value;
              element.currencyId = unmodifiedElement.currencyId;
              element.currencyCode = unmodifiedElement.currencyCode;
              element.implicit = unmodifiedElement.implicit;
              element.mandatory = unmodifiedElement.mandatory;
            }
          }
          element.isInEditing = false;
        }
      });
      set(lens)(lensLocal);
    },
    [lens, incomeList]
  );

  const [saveFinancialProductRuleIncome] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_RULE_INCOME);

  const handleSave = useCallback(
    orderNo => {
      if (!validateFinancialProductRuleIncome(lens |> get)) return;
      const lensLocal = [...(lens |> get)];
      lensLocal.forEach((element, index) => {
        if (index === orderNo) {
          element.isSaving = true;
        }
      });
      set(lens)(lensLocal);
      const elementTL = lensLocal[orderNo];
      saveFinancialProductRuleIncome({
        variables: {
          input: {
            financialProductRuleIncomeId: parseInt(elementTL.financialProductRuleIncomeId),
            financialProductRuleId: ruleId,
            readOnlyYN: elementTL.readOnlyYN,
            incomeTypeId: elementTL.incomeTypeId,
            value: parseFloat(elementTL.value),
            currencyId: elementTL.currencyId,
            implicit: elementTL.implicit,
            mandatory: elementTL.mandatory,
            orderNo: orderNo
          }
        }
      });
    },
    [lens, ruleId, saveFinancialProductRuleIncome, validateFinancialProductRuleIncome]
  );

  useSubscription(FINANCIAL_PRODUCT_RULE_INCOME_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Rules.Details.Incomes.FinancialProductRuleIncomeSaved'), 'success');
      const lensLocal = [...(lens |> get)];
      lensLocal.forEach((element, index) => {
        if (index === data.financialProductRuleIncomeUpdated.orderNo) {
          element.financialProductRuleIncomeId = data.financialProductRuleIncomeUpdated.financialProductRuleIncomeId;
          element.isInEditing = false;
          element.isSaving = false;
        }
      });
      set(lens)(lensLocal);

      client.cache.evict({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${ruleId}}`
      });

      client.cache.gc();
    }
  });

  const [deleteFinancialProductRuleIncome] = useMutation(REMOVE_FINANCIAL_PRODUCT_RULE_INCOME);

  const handleDelete = useCallback(
    (incomeId, orderNo) => {
      if (window.confirm(t('FinancialProduct.Details.Rules.Details.Incomes.DeleteFinancialProductRuleIncome'))) {
        const lensLocal = [...(lens |> get)];
        if (incomeId === undefined || incomeId === null) {
          lensLocal.splice(orderNo, 1);
          set(lens)(lensLocal);
        } else {
          deleteFinancialProductRuleIncome({
            variables: {
              input: { incomeId }
            }
          });
        }
      }
    },
    [lens, deleteFinancialProductRuleIncome, t]
  );

  useSubscription(FINANCIAL_PRODUCT_RULE_INCOME_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Rules.Details.Incomes.FinancialProductRuleIncomeRemoved'), 'success');
      const lensLocal = [...(lens |> get)];
      const objTL = lensLocal.filter(el => el.financialProductRuleIncomeId === data.financialProductRuleIncomeDeleted.incomeId);
      lensLocal.splice(lensLocal.indexOf(objTL), 1);
      set(lens)(lensLocal);
      client.cache.evict({
        id: `FinancialProductRuleEditIncomeList:{"financialProductRuleIncomeId":${data.financialProductRuleIncomeDeleted.incomeId}}`
      });
    }
  });

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return currenciesDictionaryData?.currenciesDictionary && financialProductRuleIncomeTypeList?.financialProductRuleIncomeTypeList ? (
    <FinancialProductRulesEditDetailsIncomesTable
      canWrite={canWrite}
      incomeList={lens}
      isSaved={isSaved}
      onAdd={handleAdd}
      onCloseEdit={handleCloseEdit}
      onDelete={handleDelete}
      onSave={handleSave}
      financialProductRuleIncomeCurrencyList={currenciesDictionaryData?.currenciesDictionary}
      financialProductRuleIncomeTypeList={financialProductRuleIncomeTypeList?.financialProductRuleIncomeTypeList}
      financialProductRuleIncomeValidation={financialProductRuleIncomeValidation}
    />
  ) : (
    <></>
  );
};

FinancialProductRulesEditDetailsIncomesContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  incomeList: PropTypes.array.isRequired,
  isSaved: PropTypes.bool.isRequired,
  productId: PropTypes.number
};

export default FinancialProductRulesEditDetailsIncomesContainer;
